/**
 * Custom hook for managing field selection with session storage persistence
 * Handles field selection state, ordering, and persistence across sessions
 */

import { useState, useEffect, useCallback } from 'react'
import { ExportField, SelectedField, FieldConfiguration } from '../components/FieldSelector'
import { FieldSelectionStorage } from '../services/sessionStorage'

interface UseFieldSelectionProps {
  configuration: FieldConfiguration | null
  exportType?: string
}

interface UseFieldSelectionReturn {
  fields: SelectedField[]
  filteredFields: SelectedField[]
  searchTerm: string
  setSearchTerm: (term: string) => void
  toggleField: (fieldId: string) => void
  selectAll: () => void
  deselectAll: () => void
  reorderFields: (oldIndex: number, newIndex: number) => void
  resetToDefaults: () => void
  hasStoredSelections: boolean
  enabledCount: number
  availableCount: number
}

export function useFieldSelection({
  configuration,
  exportType
}: UseFieldSelectionProps): UseFieldSelectionReturn {
  const [fields, setFields] = useState<SelectedField[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [hasStoredSelections, setHasStoredSelections] = useState(false)

  // Initialize fields when configuration changes
  useEffect(() => {
    if (!configuration) {
      setFields([])
      setHasStoredSelections(false)
      return
    }

    const exportTypeKey = exportType || configuration.export_type

    // Cleanup any invalid stored selections first
    FieldSelectionStorage.cleanupStoredSelections(
      exportTypeKey,
      configuration.fields.map(f => f.id)
    )

    // Load stored selections or use defaults
    const stored = FieldSelectionStorage.load(exportTypeKey)
    const hasStored = FieldSelectionStorage.hasStoredSelections(exportTypeKey)
    
    const initialFields = FieldSelectionStorage.applyStoredSelections(
      configuration.fields,
      stored
    )

    setFields(initialFields)
    setHasStoredSelections(hasStored)
  }, [configuration, exportType])

  // Save selections whenever fields change
  useEffect(() => {
    if (fields.length > 0 && configuration) {
      const exportTypeKey = exportType || configuration.export_type
      FieldSelectionStorage.store(exportTypeKey, fields)
    }
  }, [fields, configuration, exportType])

  // Filter fields based on search term
  const filteredFields = fields.filter(field => {
    if (!searchTerm) return true
    
    const term = searchTerm.toLowerCase()
    return (
      field.name.toLowerCase().includes(term) ||
      field.description.toLowerCase().includes(term) ||
      field.export_heading.toLowerCase().includes(term) ||
      field.api_field.toLowerCase().includes(term)
    )
  })

  // All fields are selectable - requirements auto-detected from selection

  // Toggle field enabled state
  const toggleField = useCallback((fieldId: string) => {
    setFields(prev => prev.map(field => 
      field.id === fieldId 
        ? { ...field, enabled: !field.enabled }
        : field
    ))
  }, [])

  // Select all available fields
  const selectAll = useCallback(() => {
    setFields(prev => prev.map(field => ({
      ...field,
      enabled: true  // All fields are now selectable
    })))
  }, [])

  // Deselect all fields
  const deselectAll = useCallback(() => {
    setFields(prev => prev.map(field => ({
      ...field,
      enabled: false
    })))
  }, [])

  // Reorder fields by moving from old index to new index
  const reorderFields = useCallback((oldIndex: number, newIndex: number) => {
    setFields(prev => {
      const newFields = [...prev]
      const [moved] = newFields.splice(oldIndex, 1)
      newFields.splice(newIndex, 0, moved)
      
      // Update order values
      return newFields.map((field, index) => ({
        ...field,
        order: index
      }))
    })
  }, [])

  // Reset to default configuration selections
  const resetToDefaults = useCallback(() => {
    if (!configuration) return

    const exportTypeKey = exportType || configuration.export_type
    
    // Clear stored selections
    FieldSelectionStorage.clear(exportTypeKey)
    
    // Reset to default configuration
    const defaultFields: SelectedField[] = configuration.fields.map((field, index) => ({
      ...field,
      enabled: field.default_enabled,
      order: index
    }))
    
    setFields(defaultFields)
    setHasStoredSelections(false)
  }, [configuration, exportType])

  // Calculate counts
  const enabledCount = fields.filter(field => field.enabled).length
  const availableCount = fields.length  // All fields are now available

  return {
    fields,
    filteredFields,
    searchTerm,
    setSearchTerm,
    toggleField,
    selectAll,
    deselectAll,
    reorderFields,
    resetToDefaults,
    hasStoredSelections,
    enabledCount,
    availableCount
  }
}